import type { RouteLocationNamedRaw } from 'vue-router';

/** The shape of a `useSidebarLinks()` item that the palette reads */
export interface SidebarLink {
    name: string;
    to: { name: string; params?: RouteLocationNamedRaw['params'] };
    icon?: string;
    children?: SidebarLink[];
}

export interface CommandLink {
    label: string;
    /** Unique in the palette, and what the search matches: a child's value starts with its group's label */
    value: string;
    icon?: string;
    to: SidebarLink['to'];
}

export interface CommandLinkGroup {
    heading?: string;
    links: CommandLink[];
}

/**
 * Flattens the sidebar into palette groups: one group without a heading for the links without children, then one group per link with children.
 * A parent's own link is left out, because it repeats one of its children.
 */
export function toCommandGroups(links: SidebarLink[], translate: (key: string) => string): CommandLinkGroup[] {
    const pages: CommandLink[] = [];
    const groups: CommandLinkGroup[] = [];

    for (const link of links) {
        const label = translate(link.name);

        if (!link.children?.length) {
            pages.push({ label, value: label, icon: link.icon, to: link.to });
            continue;
        }

        groups.push({
            heading: label,
            links: link.children.map((child) => {
                const childLabel = translate(child.name);

                return { label: childLabel, value: `${label} ${childLabel}`, icon: child.icon, to: child.to };
            }),
        });
    }

    return pages.length ? [{ links: pages }, ...groups] : groups;
}
